import {ValidRegionCode} from 'src/config';
import {LineItem} from 'src/features/cart/types';
import {MoneyBag} from 'src/features/pricing/types';

/**
 * Stripe products that canyon treats as accessories
 * (NextGenClip, USBCWiredCharger, Stryd5ClipPackAddOn, Stryd5WiredCharger, Stryd5ClipPack).
 * Live IDs match the production catalog. Test IDs are the same SKUs in the test catalog.
 */
const ACCESSORY_STRIPE_PRODUCT_IDS = new Set([
  'prod_UW3qKlZVnrZmSd',
  'prod_UW3rr02aGUElXz',
  'prod_T9QofZXsfzmAZ6',
  'prod_UW3s4Xmh7XpaGx',
  'prod_UW3tmH3mcc7qUk',
  'prod_T5lI7Z5fIlQ1t1',
  'prod_T9QwxppDTww4r5',
]);

/** Fixed amounts from the Accessory Shipping Stripe rates, in minor units. */
const ACCESSORY_SHIPPING_BY_REGION: Record<ValidRegionCode, MoneyBag> = {
  us: {unit_amount: 1000, currency: 'USD'},
  eu: {unit_amount: 1000, currency: 'EUR'},
  uk: {unit_amount: 2000, currency: 'GBP'},
  ca: {unit_amount: 1500, currency: 'CAD'},
  au: {unit_amount: 1500, currency: 'AUD'},
  jp: {unit_amount: 5000, currency: 'JPY'},
  gl: {unit_amount: 3500, currency: 'USD'},
};

const stripeProductIds = (item: LineItem): string[] =>
  item.selectedVariant?.fields?.stripeProducts?.map(
    (product) => product.fields.stripeProductId,
  ) ?? [];

/** True when the cart is non-empty and every Stripe product on every line is an accessory. */
export const isAccessoryOnlyCart = (lineItems: LineItem[]): boolean => {
  if (lineItems.length === 0) return false;

  return lineItems.every((item) => {
    const ids = stripeProductIds(item);
    return (
      ids.length > 0 && ids.every((id) => ACCESSORY_STRIPE_PRODUCT_IDS.has(id))
    );
  });
};

/** Regional accessory shipping, or null when the cart is not accessory-only. Flat rate, not per item. */
export const getAccessoryShipping = (
  lineItems: LineItem[],
  region: ValidRegionCode,
): MoneyBag | null => {
  if (!isAccessoryOnlyCart(lineItems)) return null;
  return ACCESSORY_SHIPPING_BY_REGION[region];
};
